import { Component, ElementRef, ViewChild } from '@angular/core';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { AuthenticationStatus, LoginType, PageBaseComponent, UtilsString } from '@pit/core';
import { IPinItTrackFrontendApplicationService, IPinItTrackFrontendAuthenticationService, PinItTrackFrontendServicesAccessor } from '@pit/frontend-shared';
import { Observable, Subscription } from 'rxjs';

@Component({
  selector: 'app-login-credentials',
  templateUrl: './pin.it.track.web.login.page.component.html',
  styleUrls: ['./pin.it.track.web.login.page.component.scss'],
  animations: [
    trigger('shakeAnimation', [
      state('idle', style({ transform: 'translateX(0)' })),
      state('shaking', style({ transform: 'translateX(0)' })),
      transition('idle => shaking', [
        animate('50ms', style({ transform: 'translateX(-10px)' })),
        animate('50ms', style({ transform: 'translateX(10px)' })),
        animate('50ms', style({ transform: 'translateX(-10px)' })),
        animate('50ms', style({ transform: 'translateX(10px)' })),
        animate('50ms', style({ transform: 'translateX(0)' }))
      ])
    ]),
    trigger('zoomOutAnimation', [
      state('normal', style({ transform: 'scale(1)', opacity: 1 })),
      state('zoomedOut', style({ transform: 'scale(0.8)', opacity: 0 })),
      transition('normal => zoomedOut', animate('400ms ease-in'))
    ])
  ]
})
export class PinItTrackWebLoginPageComponent extends PageBaseComponent {

  constructor(private readonly servicesAccessor: PinItTrackFrontendServicesAccessor) {
    super(servicesAccessor.coreServicesAccessor.applicationService);

    this.authenticationService = this.servicesAccessor.coreServicesAccessor.authenticationService;
    this.applicationServiceExt = this.servicesAccessor.coreServicesAccessor.applicationService;

    this.loggingService.debug('PITTrackWebLoginPageComponent --> constructor()');
  }

  public readonly authenticationService: IPinItTrackFrontendAuthenticationService = null;

  public readonly applicationServiceExt: IPinItTrackFrontendApplicationService = null;

  public isBusy$: Observable<boolean> = this.servicesAccessor.coreServicesAccessor.applicationService.isBusy$;

  public get canLogin(): boolean {
    return ((UtilsString.stringIsNullOrEmpty(this.mandator) === false) &&
            (UtilsString.stringIsNullOrEmpty(this.userName) === false) &&
            (UtilsString.stringIsNullOrEmpty(this.password) === false));
  }

  public get isInLogin(): boolean {
    return ((this.authenticationService.authenticationStatus === AuthenticationStatus.InLoginCheckUser) ||
      (this.authenticationService.authenticationStatus === AuthenticationStatus.InLoginUserChecked) ||
      (this.authenticationService.authenticationStatus === AuthenticationStatus.InLoginCheckPassword) ||
      (this.authenticationService.authenticationStatus === AuthenticationStatus.InLoginPasswordChecked));
  }

  public mandator: string = null;

  public password: string = null;

  public userName: string = null;

  public shakeState: string = 'idle';
  public zoomState: string = 'normal';
  public isLoginInProgress: boolean = false;

  // Validation and error handling
  public showValidationMessages: boolean = false;
  public loginErrorMessage: string = null;
  public showPassword: boolean = false;


  private authStatusSubscription: Subscription;

  @ViewChild('mandatorInput')
  private mandatorInput: ElementRef<HTMLInputElement>;

  @ViewChild('usernameInput')
  private usernameInput: ElementRef<HTMLInputElement>;

  // Enter is handled by the form submit now; no keypress handler needed.

  public async loginAsync(): Promise<void> {
    // Clear previous error messages and show validation if fields are empty
    this.loginErrorMessage = null;
    this.showValidationMessages = true;

    if (this.canLogin === true && !this.isLoginInProgress) {
      this.isLoginInProgress = true;
      this.shakeState = 'idle';
      this.zoomState = 'normal';

      // Start monitoring authentication status
      this.startAuthStatusMonitoring();

      this.servicesAccessor.eventService.sendEvent('auth', {
        type: 'login.credentials',
        mandator: this.mandator,
        userName: this.userName,
        password: this.password
      });
    }
  }

  protected override async doOnAfterViewInitAsync(): Promise<void> {
    await super.doOnAfterViewInitAsync();

    // Put the cursor into the first empty field so typing can start right away.
    const target: ElementRef<HTMLInputElement> =
      (UtilsString.stringIsNullOrEmpty(this.mandator) === false) ? this.usernameInput : this.mandatorInput;
    if (target && target.nativeElement) {
      target.nativeElement.focus();
    }

    //this.eventService.sendEvent('init');
  }

  protected override async doOnDestroyAsync(): Promise<void> {
    if (this.authStatusSubscription) {
      this.authStatusSubscription.unsubscribe();
    }
    await super.doOnDestroyAsync();
  }

  private startAuthStatusMonitoring(): void {
    if (this.authStatusSubscription) {
      this.authStatusSubscription.unsubscribe();
    }

    // Monitor authentication status changes
    const authService = this.authenticationService;
    const initialStatus = authService.authenticationStatus;
    let hasSeenProgress = false;

    // Set up a simple interval to check status changes
    let checkCount = 0;
    const maxChecks = 100; // 10 seconds maximum
    
    const statusInterval = setInterval(() => {
      const currentStatus = authService.authenticationStatus;
      
      checkCount++;
      
      // Track if we've seen any progress (intermediate states)
      if (currentStatus === AuthenticationStatus.InLoginCheckUser ||
          currentStatus === AuthenticationStatus.InLoginUserChecked ||
          currentStatus === AuthenticationStatus.InLoginCheckPassword ||
          currentStatus === AuthenticationStatus.InLoginPasswordChecked) {
        hasSeenProgress = true;
      }
      
      if (this.isLoginInProgress) {
        if (currentStatus === AuthenticationStatus.LoggedIn) {
          // Success - zoom out
          this.isLoginInProgress = false;
          this.zoomState = 'zoomedOut';
          clearInterval(statusInterval);
        } else if (currentStatus === AuthenticationStatus.LoggedOut && hasSeenProgress) {
          // Failed - we were making progress but ended up back at LoggedOut
          this.isLoginInProgress = false;
          this.triggerShakeAnimation();
          clearInterval(statusInterval);
        } else if (currentStatus === AuthenticationStatus.LoggedOut && checkCount > 30) {
          // Failed - no progress made after reasonable time (3 seconds)
          this.isLoginInProgress = false;
          this.triggerShakeAnimation();
          clearInterval(statusInterval);
        }
      }
      
      // Timeout after max checks
      if (checkCount >= maxChecks) {
        this.isLoginInProgress = false;
        if (currentStatus !== AuthenticationStatus.LoggedIn) {
          this.triggerShakeAnimation();
        }
        clearInterval(statusInterval);
      }
    }, 100);
  }

  private triggerShakeAnimation(): void {
    this.shakeState = 'shaking';
    this.loginErrorMessage = 'Anmeldung fehlgeschlagen. Bitte überprüfen Sie Ihre Eingaben.';

    // Reset shake state after animation completes
    setTimeout(() => {
      this.shakeState = 'idle';
    }, 300);
  }

  public get showBusyScreen(): boolean {
    return this.isLoginInProgress;
  }

  // Validation helper methods
  public get isMandatorInvalid(): boolean {
    return this.showValidationMessages && UtilsString.stringIsNullOrEmpty(this.mandator);
  }

  public get isUserNameInvalid(): boolean {
    return this.showValidationMessages && UtilsString.stringIsNullOrEmpty(this.userName);
  }

  public get isPasswordInvalid(): boolean {
    return this.showValidationMessages && UtilsString.stringIsNullOrEmpty(this.password);
  }

  public get mandatorValidationMessage(): string {
    if (this.showValidationMessages && UtilsString.stringIsNullOrEmpty(this.mandator)) {
      return 'Mandant ist erforderlich';
    }
    return '';
  }

  public togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }


}
